import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import {
  PATIENT_TABLE_GRID,
  type PatientSortBy,
  type PatientSortDir,
  type PatientTableHeaderProps,
} from "@/frontend/types/dashboard/patients";

/** Shared typography of every header cell label, sortable or not. */
const HEADER_LABEL_CLASS =
  "flex h-9 items-center gap-1 text-meta font-medium uppercase tracking-wide text-foreground";

/**
 * A sortable header cell. Clicking cycles none → asc → desc → none; clearing
 * hands the table back to its lastActivity-desc default.
 */
function SortableHead({
  label,
  column,
  sortBy,
  sortDir,
  onSortChange,
}: {
  label: string;
  column: PatientSortBy;
  sortBy: PatientSortBy | undefined;
  sortDir: PatientSortDir | undefined;
  onSortChange: PatientTableHeaderProps["onSortChange"];
}) {
  const activeDir = sortBy === column ? (sortDir ?? "desc") : undefined;
  const Icon = activeDir === "asc" ? ArrowUp : activeDir === "desc" ? ArrowDown : ChevronsUpDown;

  /** Advances this column's sort through the three-state cycle. */
  const cycle = () => {
    if (sortBy !== column) onSortChange(column, "asc");
    else if (activeDir === "asc") onSortChange(column, "desc");
    else onSortChange(undefined, undefined);
  };

  return (
    <div className="pr-4">
      <button
        type="button"
        onClick={cycle}
        className={cn(
          HEADER_LABEL_CLASS,
          "transition-colors hover:text-foreground",
          activeDir && "text-foreground",
        )}
        aria-label={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        <Icon className={cn("size-3", activeDir ? "opacity-100" : "opacity-60")} />
      </button>
    </div>
  );
}

/** A non-interactive column label, styled to match the sortable cells. */
function PlainHead({ label }: { label: string }) {
  return (
    <div className="pr-4">
      <span className={HEADER_LABEL_CLASS}>{label}</span>
    </div>
  );
}

/**
 * The patient table's header row, with sortable Patient, Records and Last sync
 * cells and plain Sources and Status labels. It owns no state, and sort changes
 * are written back through `onSortChange` for the route to persist in the URL.
 */
export function PatientTableHeader({ sortBy, sortDir, onSortChange }: PatientTableHeaderProps) {
  return (
    <div
      className={cn(
        PATIENT_TABLE_GRID,
        "h-9 shrink-0 overflow-hidden border-b bg-muted/40 px-6 [scrollbar-gutter:stable]",
      )}
    >
      <SortableHead label="Patient" column="name" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />

      <PlainHead label="Sources" />

      <SortableHead label="Records" column="records" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />

      <PlainHead label="Status" />

      <SortableHead
        label="Last sync"
        column="lastActivity"
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={onSortChange}
      />

      <div aria-hidden />
    </div>
  );
}
